import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Commercants from './pages/Commercants';
import Materiels from './pages/Materiels';
import CommerceTaxonomy from './pages/CommerceTaxonomy';
import CommercantDetail from './pages/CommercantDetail';
import Produits from './pages/Produits';
import CommandesWrapper from './pages/CommandesWrapper';
import MerchantDocuments from './pages/MerchantDocuments';
import Clients from './pages/Clients';
import ClientProfile from './pages/ClientProfileComplete';
import PromotionsManager from './components/promotions/PromotionsManager';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Chat from './pages/Chat';
import Settings from './pages/Settings';
import Ingredients from './pages/Ingredients';
import Templates from './pages/Templates';
import Livreurs from './pages/Livreurs';
import AvisPlateforme from './pages/AvisPlateforme';
import ImportIA from './pages/ImportIA';
import Fidelite from './pages/Fidelite';
import Prospection from './pages/Prospection';
import Nr2jDashboard from './pages/nr2j/Nr2jDashboard';
import Nr2jClients from './pages/nr2j/Nr2jClients';
import Nr2jOrganizationDetail from './pages/nr2j/Nr2jOrganizationDetail';
import Nr2jTasks from './pages/nr2j/Nr2jTasks';
import Nr2jCatalog from './pages/nr2j/Nr2jCatalog';
import Nr2jQuotesList from './pages/nr2j/Nr2jQuotesList';
import Nr2jQuoteEditor from './pages/nr2j/Nr2jQuoteEditor';
import Nr2jQuoteDetail from './pages/nr2j/Nr2jQuoteDetail';
import Nr2jContractsList from './pages/nr2j/Nr2jContractsList';
import Nr2jContractEditor from './pages/nr2j/Nr2jContractEditor';
import Nr2jContractDetail from './pages/nr2j/Nr2jContractDetail';
import Nr2jInvoicesList from './pages/nr2j/Nr2jInvoicesList';
import Nr2jInvoiceEditor from './pages/nr2j/Nr2jInvoiceEditor';
import Nr2jInvoiceDetail from './pages/nr2j/Nr2jInvoiceDetail';
import Nr2jCreditNoteDetail from './pages/nr2j/Nr2jCreditNoteDetail';
import Nr2jTeam from './pages/nr2j/Nr2jTeam';
import Nr2jMyTeam from './pages/nr2j/Nr2jMyTeam';
import Nr2jMyTeamDetail from './pages/nr2j/Nr2jMyTeamDetail';
import Nr2jApprovals from './pages/nr2j/Nr2jApprovals';
import Nr2jActivity from './pages/nr2j/Nr2jActivity';
import Layout from './components/Layout';
import CarteCommercants from './pages/CarteCommercants';
import Caisse from './pages/Caisse';
import CaisseKpi from './pages/CaisseKpi';
import Staff from './pages/Staff';
import Praticiens from './pages/Praticiens';
import Equipe from './pages/Equipe';
import EquipePresences from './pages/EquipePresences';
import EquipeMember from './pages/EquipeMember';
import Planning from './pages/Planning';
import AujourdHui from './pages/AujourdHui';
import ProtectedRoute from './components/ProtectedRoute';
import {
  restoreAssistanceContext,
  ensureAssistanceSessionFresh,
} from './utils/assistanceSession';
import { nr2jStaffHomePath } from './config/nav/superAdminNav';

console.log('🚀 App.jsx chargé');

// Fonction pour déterminer la route de redirection selon le rôle de l'utilisateur
const getRedirectPath = () => {
  try {
    const userData = localStorage.getItem('admin_user');
    if (userData) {
      const user = JSON.parse(userData);
      // Si c'est un COMMERCANT avec un commercantId, rediriger vers sa page de commerce
      if (user.role === 'COMMERCANT' && user.commercantId) {
        return `/commercants/${user.commercantId}`;
      }
      if (user.role === 'NR2J_STAFF') {
        return nr2jStaffHomePath(user.platformPermissions || []);
      }
    }
  } catch (error) {
    console.error('Erreur parsing user data pour redirection:', error);
  }
  // Par défaut, rediriger vers le dashboard (SUPER_ADMIN ou ADMIN)
  return '/dashboard';
};

function readAuthBootstrap() {
  // A1.2 — purge synchrone AVANT tout rendu / appel API / WS si expiresAt dépassé.
  ensureAssistanceSessionFresh();

  const token = localStorage.getItem('admin_access_token') || localStorage.getItem('admin_token');
  const userData = localStorage.getItem('admin_user');
  if (!token || !userData) {
    return { isAuthenticated: false, userRole: 'SUPER_ADMIN' };
  }
  try {
    const user = JSON.parse(userData);
    return {
      isAuthenticated: true,
      userRole: user.role === 'super-admin' ? 'SUPER_ADMIN' : (user.role || 'SUPER_ADMIN'),
    };
  } catch (error) {
    console.error('Erreur parsing user data:', error);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_user');
    return { isAuthenticated: false, userRole: 'SUPER_ADMIN' };
  }
}

function hasStoredAdminSession() {
  const token = localStorage.getItem('admin_access_token') || localStorage.getItem('admin_token');
  const userData = localStorage.getItem('admin_user');
  return Boolean(token && userData);
}

function App() {
  const bootstrap = readAuthBootstrap();
  const [isAuthenticated, setIsAuthenticated] = useState(bootstrap.isAuthenticated);
  const [userRole, setUserRole] = useState(bootstrap.userRole);
  // Hydratation F5 : uniquement si session assistance encore valide (déjà purgée sinon).
  const [impersonatedUser, setImpersonatedUser] = useState(() => restoreAssistanceContext());

  return (
    <ErrorBoundary>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated && hasStoredAdminSession()
                ? <Navigate to={getRedirectPath()} replace />
                : <Login setIsAuthenticated={setIsAuthenticated} />
            } 
          />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route 
            path="/" 
            element={
              isAuthenticated ? <Navigate to={getRedirectPath()} /> : <Navigate to="/login" />
            } 
          />
          {isAuthenticated ? (
            <Route path="/" element={<Layout setIsAuthenticated={setIsAuthenticated} userRole={userRole} impersonatedUser={impersonatedUser} setImpersonatedUser={setImpersonatedUser} />}>
              <Route path="dashboard" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Routes super admin uniquement */}
              <Route path="commercants" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <Commercants setImpersonatedUser={setImpersonatedUser} />
                </ProtectedRoute>
              } />
              <Route path="carte-commercants" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <CarteCommercants />
                </ProtectedRoute>
              } />
              <Route path="materiels" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <Materiels />
                </ProtectedRoute>
              } />
              <Route path="taxonomie-commerce" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <CommerceTaxonomy />
                </ProtectedRoute>
              } />
              <Route path="commercants/:id" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <CommercantDetail setImpersonatedUser={setImpersonatedUser} />
                </ProtectedRoute>
              } />
              <Route path="produits" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Produits />
                </ProtectedRoute>
              } />
              <Route path="commandes" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <CommandesWrapper />
                </ProtectedRoute>
              } />
              <Route path="documents" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'COMMERCANT']}>
                  <MerchantDocuments />
                </ProtectedRoute>
              } />
              <Route path="caisse" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Caisse />
                </ProtectedRoute>
              } />
              <Route path="caisse/kpi" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <CaisseKpi />
                </ProtectedRoute>
              } />
              <Route path="clients" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Clients />
                </ProtectedRoute>
              } />
              <Route path="clients/:clientId" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <ClientProfile />
                </ProtectedRoute>
              } />
              <Route path="promotions" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <PromotionsManager />
                </ProtectedRoute>
              } />
              <Route path="fidelite" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <Fidelite />
                </ProtectedRoute>
              } />
              <Route path="prospection" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_PROSPECTS_VIEW">
                  <Prospection />
                </ProtectedRoute>
              } />
              <Route path="nr2j" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_DASHBOARD_VIEW">
                  <Nr2jDashboard />
                </ProtectedRoute>
              } />
              <Route path="nr2j/clients" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_ORGANIZATIONS_VIEW">
                  <Nr2jClients />
                </ProtectedRoute>
              } />
              <Route path="nr2j/clients/:id" element={
                <ProtectedRoute
                  allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']}
                  requiredAnyPermissions={['NR2J_PROSPECTS_VIEW', 'NR2J_ORGANIZATIONS_VIEW']}
                >
                  <Nr2jOrganizationDetail />
                </ProtectedRoute>
              } />
              <Route path="nr2j/taches" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_TASKS_VIEW">
                  <Nr2jTasks />
                </ProtectedRoute>
              } />
              <Route path="nr2j/catalogue" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_CATALOG_VIEW">
                  <Nr2jCatalog />
                </ProtectedRoute>
              } />
              <Route path="nr2j/devis" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_QUOTES_VIEW">
                  <Nr2jQuotesList />
                </ProtectedRoute>
              } />
              <Route path="nr2j/devis/nouveau" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_QUOTES_EDIT">
                  <Nr2jQuoteEditor />
                </ProtectedRoute>
              } />
              <Route path="nr2j/devis/:id" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_QUOTES_VIEW">
                  <Nr2jQuoteDetail />
                </ProtectedRoute>
              } />
              <Route path="nr2j/contrats" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_CONTRACTS_VIEW">
                  <Nr2jContractsList />
                </ProtectedRoute>
              } />
              <Route path="nr2j/contrats/nouveau" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_CONTRACTS_EDIT">
                  <Nr2jContractEditor />
                </ProtectedRoute>
              } />
              <Route path="nr2j/contrats/:id" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_CONTRACTS_VIEW">
                  <Nr2jContractDetail />
                </ProtectedRoute>
              } />
              <Route path="nr2j/facturation" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_INVOICES_VIEW">
                  <Nr2jInvoicesList />
                </ProtectedRoute>
              } />
              <Route path="nr2j/facturation/nouveau" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_INVOICES_EDIT">
                  <Nr2jInvoiceEditor />
                </ProtectedRoute>
              } />
              <Route path="nr2j/facturation/:id" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_INVOICES_VIEW">
                  <Nr2jInvoiceDetail />
                </ProtectedRoute>
              } />
              <Route path="nr2j/avoirs/:id" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_CREDIT_NOTES_VIEW">
                  <Nr2jCreditNoteDetail />
                </ProtectedRoute>
              } />
              <Route path="nr2j/equipe" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_USERS_MANAGE">
                  <Nr2jTeam />
                </ProtectedRoute>
              } />
              <Route path="nr2j/mon-equipe" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']}>
                  <Nr2jMyTeam />
                </ProtectedRoute>
              } />
              <Route path="nr2j/mon-equipe/:userId" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']}>
                  <Nr2jMyTeamDetail />
                </ProtectedRoute>
              } />
              <Route path="nr2j/suivi-commercial" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']}>
                  <Nr2jMyTeam />
                </ProtectedRoute>
              } />
              <Route path="nr2j/a-valider" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']}>
                  <Nr2jApprovals />
                </ProtectedRoute>
              } />
              <Route path="nr2j/activite" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'NR2J_STAFF']} requiredPermission="NR2J_ACTIVITY_ALL_VIEW">
                  <Nr2jActivity />
                </ProtectedRoute>
              } />
              <Route path="analytics" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="analytics/:detailKey" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="analytics/:detailKey" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="chat" element={
                <ProtectedRoute allowedRoles={['COMMERCANT']}>
                  <Chat />
                </ProtectedRoute>
              } />
              <Route path="notifications" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Notifications />
                </ProtectedRoute>
              } />
              <Route path="settings" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="ingredients" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Ingredients />
                </ProtectedRoute>
              } />
              <Route path="templates" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <Templates />
                </ProtectedRoute>
              } />
              <Route path="equipe/presences" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <EquipePresences />
                </ProtectedRoute>
              } />
              <Route path="equipe" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Equipe />
                </ProtectedRoute>
              } />
              <Route path="equipe/:memberId" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <EquipeMember />
                </ProtectedRoute>
              } />
              <Route path="planning" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Planning />
                </ProtectedRoute>
              } />
              <Route path="aujourdhui" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <AujourdHui />
                </ProtectedRoute>
              } />
              <Route path="staff" element={
                <Navigate to="/equipe?legacy=staff" replace />
              } />
              <Route path="praticiens" element={
                <Navigate to="/equipe?legacy=praticiens" replace />
              } />
              <Route path="staff-legacy" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Staff />
                </ProtectedRoute>
              } />
              <Route path="praticiens-legacy" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Praticiens />
                </ProtectedRoute>
              } />
              <Route path="livreurs" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMMERCANT']}>
                  <Livreurs />
                </ProtectedRoute>
              } />
              <Route path="avis-plateforme" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <AvisPlateforme />
                </ProtectedRoute>
              } />
              <Route path="import-ia" element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                  <ImportIA />
                </ProtectedRoute>
              } />
            </Route>
          ) : (
            <Route path="*" element={<Navigate to="/login" />} />
          )}
          </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;